import React, { useEffect, useMemo, useState } from 'react';
import { getMyServiceHistory, submitRequestReply } from '../api/userApi';
import '../Style/DonationFlow.css';

const STATUS_STYLES = {
  PENDING: { color: '#f59e0b', label: 'Pending', icon: '⏳' },
  APPROVED: { color: '#6366f1', label: 'Approved', icon: '✅' },
  EXECUTED: { color: '#22c55e', label: 'Executed', icon: '🎯' },
  REJECTED: { color: '#ef4444', label: 'Rejected', icon: '❌' },
};

const TYPE_LABELS = {
  GOAL_CHECK: { label: 'Goal Check', icon: '🎯' },
  FREE_TEXT: { label: 'Custom Request', icon: '✏️' },
};

const formatMoney = (amount) => {
  const parsed = Number(amount || 0);
  return `$${parsed.toFixed(2)}`;
};

const formatDate = (date) => {
  if (!date) return 'Unknown date';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ServiceRequestHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyInputs, setReplyInputs] = useState({});
  const [replyLoading, setReplyLoading] = useState({});

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    getMyServiceHistory()
      .then((data) => setHistory(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to load history', err))
      .finally(() => setLoading(false));
  }, []);

  const handleReply = async (requestId) => {
    const reply = replyInputs[requestId];

    if (!reply?.trim()) return;

    setReplyLoading((prev) => ({ ...prev, [requestId]: true }));

    try {
      await submitRequestReply(requestId, reply);

      setHistory((prev) =>
        prev.map((request) =>
          request.id === requestId
            ? {
                ...request,
                userReply: reply,
                userRepliedAt: new Date().toISOString(),
              }
            : request,
        ),
      );

      setReplyInputs((prev) => ({ ...prev, [requestId]: '' }));
    } catch (err) {
      console.error('Reply failed', err);
    } finally {
      setReplyLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const counts = useMemo(() => {
    return {
      all: history.length,
      pending: history.filter((r) => r.status === 'PENDING').length,
      executed: history.filter((r) => r.status === 'EXECUTED').length,
      rejected: history.filter((r) => r.status === 'REJECTED').length,
    };
  }, [history]);

  const filteredHistory = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return history.filter((request) => {
      const matchesStatus = statusFilter === 'ALL' || request.status === statusFilter;

      const matchesType = typeFilter === 'ALL' || request.requestType === typeFilter;

      const searchableText = [
        request.requestType,
        request.status,
        request.goalTitle,
        request.targetDate,
        request.customMessage,
        request.adminNote,
        request.userReply,
        request.adminFollowUp,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !normalizedSearch || searchableText.includes(normalizedSearch);

      return matchesStatus && matchesType && matchesSearch;
    });
  }, [history, statusFilter, typeFilter, searchTerm]);

  if (loading) {
    return <div className="loading-text">Loading your requests...</div>;
  }

  return (
    <div className="history-container">
      <div className="history-page-header">
        <div>
          <h2 className="step-title">My Requests</h2>
          <p className="history-subtitle">Track your Goal Check and Custom Request history.</p>
        </div>
      </div>

      <div className="history-summary-grid">
        <div className="history-summary-card">
          <span className="summary-number">{counts.all}</span>
          <span className="summary-label">Total</span>
        </div>

        <div className="history-summary-card">
          <span className="summary-number">{counts.pending}</span>
          <span className="summary-label">Pending</span>
        </div>

        <div className="history-summary-card">
          <span className="summary-number">{counts.executed}</span>
          <span className="summary-label">Executed</span>
        </div>

        <div className="history-summary-card">
          <span className="summary-number">{counts.rejected}</span>
          <span className="summary-label">Rejected</span>
        </div>
      </div>

      <div className="history-controls">
        <input
          className="history-search-input"
          type="text"
          placeholder="Search requests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="history-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="ALL">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="EXECUTED">Executed</option>
          <option value="REJECTED">Rejected</option>
        </select>

        <select
          className="history-filter-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="ALL">All Types</option>
          <option value="GOAL_CHECK">Goal Check</option>
          <option value="FREE_TEXT">Custom Request</option>
        </select>
      </div>

      {history.length === 0 ? (
        <div className="history-empty-state">
          <p className="history-empty-title">No requests yet.</p>
          <p className="history-empty">Once you submit a service request, it will appear here.</p>
        </div>
      ) : filteredHistory.length === 0 ? (
        <div className="history-empty-state">
          <p className="history-empty-title">No matching requests.</p>
          <p className="history-empty">Try changing your filters or search term.</p>
        </div>
      ) : (
        <div className="history-list compact">
          {filteredHistory.map((req) => {
            const status = STATUS_STYLES[req.status] || {
              color: '#64748b',
              label: req.status,
              icon: '•',
            };

            const type = TYPE_LABELS[req.requestType] || {
              label: req.requestType,
              icon: '📄',
            };

            return (
              <div key={req.id} className="history-card compact-card">
                <div className="history-card-main">
                  <div className="history-left">
                    <div className="history-type-row">
                      <span className="history-type">
                        {type.icon} {type.label}
                      </span>

                      <span
                        className="history-status-pill"
                        style={{
                          color: status.color,
                          borderColor: status.color,
                        }}
                      >
                        {status.icon} {status.label}
                      </span>
                    </div>

                    <div className="history-detail">
                      {req.requestType === 'GOAL_CHECK' ? (
                        <span>
                          <strong>{req.goalTitle || 'Goal'}</strong>
                          {req.targetDate && <> — {req.targetDate}</>}
                        </span>
                      ) : (
                        <span>{req.customMessage || 'No message provided.'}</span>
                      )}
                    </div>
                  </div>

                  <div className="history-right">
                    <span className="history-amount">{formatMoney(req.donationAmount)}</span>
                    <span className="history-date">{formatDate(req.createdAt)}</span>
                  </div>
                </div>

                {req.adminNote && (
                  <div className="history-note">
                    <span className="reply-label">
                      {req.adminNote.toLowerCase().includes('automatically')
                        ? 'System Note:'
                        : 'Admin Note:'}
                    </span>{' '}
                    {req.adminNote}
                  </div>
                )}

                {req.status === 'REJECTED' && (
                  <div className="reply-section">
                    {req.userReply ? (
                      <div className="history-user-reply">
                        <span className="reply-label">Your reply:</span> {req.userReply}
                      </div>
                    ) : (
                      <div className="reply-input-wrapper">
                        <textarea
                          className="reply-textarea"
                          rows={2}
                          maxLength={1000}
                          placeholder="Reply to this rejection..."
                          value={replyInputs[req.id] || ''}
                          onChange={(e) =>
                            setReplyInputs((prev) => ({
                              ...prev,
                              [req.id]: e.target.value,
                            }))
                          }
                        />

                        <button
                          className="reply-btn"
                          onClick={() => handleReply(req.id)}
                          disabled={replyLoading[req.id]}
                        >
                          {replyLoading[req.id] ? 'Sending...' : 'Send Reply'}
                        </button>
                      </div>
                    )}

                    {req.adminFollowUp && (
                      <div className="history-note">
                        <span className="reply-label">Admin follow-up:</span> {req.adminFollowUp}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServiceRequestHistory;
